import {
    ChangeDetectionStrategy,
    Component,
    output,
    inject,
    HostBinding,
    input,
    effect,
    untracked,
} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {DateTime} from 'luxon';
import {
    InputDropdownCheckboxesComponent,
    SelectableItem,
    InputTextGroupComponent,
} from '@brunata-angular/ui';
import {InputDateGroupComponent} from '@brunata-angular/ui';
import {fromControl} from '@brunata-angular/util';
import {map, skip} from 'rxjs';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

export type ResidentsFilterData = {
    proBraLoc: string | null;
    name: string | null;
    email: string | null;
    phoneNumber: string | null;
    occupationDate: DateTime | null;
    occupationEndDate: DateTime | null;
    onlineAccess: SelectableItem[];
    noEmail: boolean;
    noPhone: boolean;
};

@Component({
    selector: 'app-residents-filter',
    standalone: true,
    imports: [
        ReactiveFormsModule,
        InputTextGroupComponent,
        InputDropdownCheckboxesComponent,
        InputDateGroupComponent,
    ],
    templateUrl: './residents-filter.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styles: [
        `
            :host {
                display: contents;
            }
        `,
    ],
})
export class ResidentsFilterComponent {
    readonly filterChanged = output<ResidentsFilterData>();
    readonly onlineAccessOptions = input<SelectableItem[]>([]);
    readonly initialOnlineAccess = input<SelectableItem[]>([]);
    readonly initialFilter = input<ResidentsFilterData | null>(null);
    readonly showNoEmailFilter = input<boolean>(false);
    readonly showNoPhoneFilter = input<boolean>(false);
    readonly showOnlineAccess = input<boolean>(false);

    private readonly formBuilder = inject(FormBuilder);

    readonly formGroupFilter = this.formBuilder.group({
        proBraLoc: new FormControl<string | null>(''),
        name: new FormControl<string | null>(''),
        email: new FormControl<string | null>(''),
        phoneNumber: new FormControl<string | null>(''),
        occupationDate: new FormControl<DateTime | null>(null),
        occupationEndDate: new FormControl<DateTime | null>(null),
        onlineAccess: new FormControl<SelectableItem[]>([], {nonNullable: true}),
        noEmail: new FormControl<boolean>(false, {nonNullable: true}),
        noPhone: new FormControl<boolean>(false, {nonNullable: true}),
    });

    constructor() {
        // One-way sync: available options -> update form control if it's empty
        effect(() => {
            const options = this.onlineAccessOptions();
            const currentVal = this.formGroupFilter.controls.onlineAccess.value;
            // Only initialize if current value is empty and we have new options
            if (currentVal.length === 0 && options.length > 0) {
                // By default, let's select all if it's the first initialization?
                // Actually, the parent `initialOnlineAccess` already handles this.
                // But `InputDropdownCheckboxesComponent` needs the full list to display anything.
                this.formGroupFilter.controls.onlineAccess.setValue(
                    options.map(opt => ({...opt, selected: false})),
                    {emitEvent: false},
                );
            }
        });

        // One-way sync from parent: when initialOnlineAccess changes, update form if current is empty
        effect(() => {
            const initial = this.initialOnlineAccess();
            if (initial.length > 0) {
                this.formGroupFilter.patchValue({onlineAccess: initial}, {emitEvent: false});
            }
        });

        // Restore the full filter once from a session snapshot. Parent only sets this on initial
        // load when a snapshot exists, so the effect fires once and does not fight live edits.
        effect(() => {
            const restored = this.initialFilter();
            if (restored == null) {
                return;
            }
            this.formGroupFilter.patchValue(restored, {emitEvent: false});
            this.syncTextFilterDisabledState();
        });

        // A "no value" checkbox and its free-text search are mutually exclusive: ticking
        // "No email" / "No phone" clears the matching text filter and disables it, so the
        // two can never contradict each other.
        this.formGroupFilter.controls.noEmail.valueChanges
            .pipe(takeUntilDestroyed())
            .subscribe(checked =>
                this.toggleTextFilter(this.formGroupFilter.controls.email, checked),
            );
        this.formGroupFilter.controls.noPhone.valueChanges
            .pipe(takeUntilDestroyed())
            .subscribe(checked =>
                this.toggleTextFilter(this.formGroupFilter.controls.phoneNumber, checked),
            );

        // The building's "no value" filter availability can resolve after a snapshot restore. When
        // it does, re-sync the disabled state: a hidden checkbox must never strand its text field
        // disabled (e.g. a snapshot with noEmail=true restored where everyone now has an email).
        effect(() => {
            this.showNoEmailFilter();
            this.showNoPhoneFilter();
            untracked(() => this.syncTextFilterDisabledState());
        });

        fromControl(this.formGroupFilter)
            .pipe(
                skip(1), // Skip initial value from startWith
                map(() => this.formGroupFilter.getRawValue()),
                takeUntilDestroyed(),
            )
            .subscribe(value => {
                this.filterChanged.emit(value);
            });
    }

    @HostBinding('[formGroup]') get formGroupBinding(): FormGroup {
        return this.formGroupFilter;
    }

    setNoEmail(noEmail: boolean): void {
        this.formGroupFilter.controls.noEmail.setValue(noEmail);
    }

    setNoPhone(noPhone: boolean): void {
        this.formGroupFilter.controls.noPhone.setValue(noPhone);
    }

    reset(): void {
        // Re-enable the text filters in case a "no value" checkbox had disabled them.
        this.formGroupFilter.controls.email.enable({emitEvent: false});
        this.formGroupFilter.controls.phoneNumber.enable({emitEvent: false});
        this.formGroupFilter.reset(
            {
                proBraLoc: '',
                name: '',
                email: '',
                phoneNumber: '',
                occupationDate: null,
                occupationEndDate: null,
                onlineAccess: this.onlineAccessOptions().map(opt => ({...opt, selected: false})),
                noEmail: false,
                noPhone: false,
            },
            {emitEvent: false},
        );
        this.filterChanged.emit(this.formGroupFilter.getRawValue());
    }

    private toggleTextFilter(control: FormControl<string | null>, disabled: boolean): void {
        if (disabled) {
            control.setValue('', {emitEvent: false});
            control.disable({emitEvent: false});
        } else {
            control.enable({emitEvent: false});
        }
    }

    private syncTextFilterDisabledState(): void {
        // Only a visible "no value" checkbox may disable its text filter; otherwise the field would
        // be stuck disabled with no checkbox to clear it. Flags read untracked so callers that run
        // inside an effect (the restore effect) don't start re-patching the form on flag changes.
        this.toggleTextFilter(
            this.formGroupFilter.controls.email,
            untracked(this.showNoEmailFilter) && this.formGroupFilter.controls.noEmail.value,
        );
        this.toggleTextFilter(
            this.formGroupFilter.controls.phoneNumber,
            untracked(this.showNoPhoneFilter) && this.formGroupFilter.controls.noPhone.value,
        );
    }
}
